/**
 * An on and off switch.
 *
 * A real checkbox with `role="switch"`, so it is reachable by keyboard (Space toggles it) and a
 * screen reader says "switch, on". The input is hidden and the track beside it is drawn from its
 * state with `peer-*`, so the look cannot drift from the value.
 *
 * The track is sunken and the knob is raised, like the rest of the tactile UI: an inner shadow in
 * the track, a small drop shadow under the knob.
 *
 * Alone, it needs `label` (a table cell whose column header names it). Inside `ToggleRow` the row's
 * own label names it, and `label` is left out.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The accessible name, when no visible label wraps the switch. */
  label?: string;
  disabled?: boolean;
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <input
        type="checkbox"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="peer absolute inset-0 m-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
      {/*
        The knob is a pseudo element rather than a nested span, because Tailwind's `peer-checked:`
        only reaches a sibling of the input. `peer-checked:after:` reaches that sibling's own
        ::after, which is the one selector that can move a knob inside a track.
      */}
      <span
        aria-hidden="true"
        className="sk-track sk-switch pointer-events-none relative h-7 w-12 rounded-pill transition-colors duration-150 after:absolute after:top-1 after:left-1 after:size-5 after:rounded-full after:transition-transform after:duration-150 after:content-[''] peer-checked:after:translate-x-5 peer-disabled:opacity-45 peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-soft"
      />
    </span>
  );
}
